import { useRef, useState } from 'react';
import { Button, Card, Tag } from '../../merchant/ui/primitives.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import { approveSavedScript, type SavedScriptView } from './savedBriefApi.js';
const copy = {
  en: {
    title: 'Review your script',
    preparing: 'Preparing your script',
    unavailable: 'Script preparation needs attention',
    held: 'Script generation is not enabled for this run yet. Your selected idea is saved; no model or video charge has been started.',
    review: 'Claim review',
    caution:
      'Automated review is advisory, not a guarantee of factual accuracy. Check names, offers, claims and visuals before approval.',
    spoken: 'Voice-over',
    screen: 'On screen',
    visual: 'Visual direction',
    none: 'None',
    approve: 'Approve this script',
    approved: 'Script approved',
    approving: 'Saving approval…',
    fail: 'Approval could not be saved. Reload the current script before retrying.',
    historical: 'Historical version — read only',
    blocked: 'Review findings need attention before approval. Your draft is preserved.',
    production:
      'Approval saved. Video production is not configured for this saved-script path. No video has been generated or charged.',
    clear: 'No issues found by automated review',
    combined: 'Across-scene findings',
    hook: 'Opening hook',
    cta: 'Call to action',
    pending:
      'Your selected idea is saved. Script preparation and review will appear here when complete.',
  },
  ar: {
    title: 'راجع النص',
    preparing: 'جارٍ إعداد النص',
    unavailable: 'إعداد النص يحتاج إلى متابعة',
    held: 'توليد النص غير مفعّل لهذا التشغيل بعد. فكرتك المختارة محفوظة؛ لم يبدأ أي طلب مدفوع للنموذج أو الفيديو.',
    review: 'مراجعة الادعاءات',
    caution:
      'المراجعة الآلية استشارية وليست ضماناً لصحة المعلومات. راجع الأسماء والعروض والادعاءات والمشاهد قبل الاعتماد.',
    spoken: 'التعليق الصوتي',
    screen: 'على الشاشة',
    visual: 'وصف المشهد',
    none: 'لا شيء',
    approve: 'اعتماد هذا النص',
    approved: 'تم اعتماد النص',
    approving: 'جارٍ حفظ الاعتماد…',
    fail: 'تعذّر حفظ الاعتماد. أعد تحميل النص الحالي قبل المحاولة.',
    historical: 'إصدار سابق — للقراءة فقط',
    blocked: 'تحتاج نتائج المراجعة إلى متابعة قبل الاعتماد. احتفظنا بالمسودة.',
    production:
      'تم حفظ الاعتماد. إنتاج الفيديو غير مهيّأ لمسار النص المحفوظ. لم يتم إنشاء فيديو أو احتساب تكلفته.',
    clear: 'لم ترصد المراجعة الآلية ملاحظات',
    combined: 'ملاحظات بين المشاهد',
    hook: 'افتتاحية النص',
    cta: 'الدعوة إلى الإجراء',
    pending: 'فكرتك المختارة محفوظة. سيظهر النص ونتائج مراجعته هنا عند اكتمالهما.',
  },
};
export function SavedScriptReview({
  view,
  language,
  readOnly,
  onRefresh,
}: {
  view: SavedScriptView;
  language: 'en' | 'ar';
  readOnly: boolean;
  onRefresh: () => Promise<void>;
}) {
  const ui = copy[language],
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false);
  const intent = useRef<{ hash: string | null; key: string } | null>(null);
  async function approve() {
    if (busy || !view.canApprove || readOnly) return;
    if (intent.current?.hash !== view.resultHash)
      intent.current = { hash: view.resultHash, key: newIdempotencyKey() };
    setBusy(true);
    setError(false);
    try {
      await approveSavedScript(view, intent.current.key);
      await onRefresh();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  if (view.script === null)
    return (
      <Card>
        <h2 className="mj-ws-h2">
          {view.preparationAuthorized && view.state !== 'BLOCKED'
            ? ui.preparing
            : ui.unavailable}
        </h2>
        <p role="status">
          {!view.preparationAuthorized
            ? ui.held
            : view.state === 'BLOCKED'
              ? ui.blocked
              : ui.pending}
        </p>
      </Card>
    );
  const findings =
    view.review?.report.units.flatMap((unit) =>
      unit.spans
        .filter((span) => !['SUPPORTED', 'NON_FACTUAL'].includes(span.classification))
        .map((span) => ({ ...span, unit: unit.id })),
    ) ?? [];
  return (
    <section aria-label={ui.title} className="mj-ws-script-review">
      <h2 className="mj-ws-h2">{ui.title}</h2>
      <p lang={view.script.outputLocale} dir="auto">
        {view.script.title}
      </p>
      <p>
        <strong>{ui.hook}: </strong>
        <span lang={view.script.outputLocale} dir="auto">
          {view.script.hook}
        </span>
      </p>
      {readOnly || !view.isCurrent ? <Tag>{ui.historical}</Tag> : null}
      <ol className="mj-ws-saved-scenes">
        {view.script.scenes.map((scene) => (
          <li key={scene.number}>
            <Card>
              <h3 className="mj-ws-h3">
                <bdi>
                  {scene.startSeconds}–{scene.endSeconds}s
                </bdi>
              </h3>
              <dl className="mj-ws-rdl">
                <dt>{ui.spoken}</dt>
                <dd lang={view.script?.outputLocale} dir="auto">
                  {scene.spokenText}
                </dd>
                <dt>{ui.screen}</dt>
                <dd dir="auto">{scene.onScreenText ?? ui.none}</dd>
                <dt>{ui.visual}</dt>
                <dd dir="auto">{scene.visualDescription}</dd>
              </dl>
            </Card>
          </li>
        ))}
      </ol>
      <Card>
        <h3 className="mj-ws-h3">{ui.cta}</h3>
        <p lang={view.script.outputLocale} dir="auto">
          {view.script.cta?.text ?? ui.none}
        </p>
        {view.script.cta && (
          <p className="mj-ws-full-text" dir="auto">
            {view.script.cta.destination}
          </p>
        )}
      </Card>
      <Card>
        <h3 className="mj-ws-h3">{ui.review}</h3>
        <p className="mj-ws-sm">{ui.caution}</p>
        <p>{view.review?.disposition === 'NO_ISSUES_FOUND' ? ui.clear : ui.blocked}</p>
        {findings.map((f, i) => (
          <blockquote key={i}>
            <p dir="auto">{f.text}</p>
            <p className="mj-ws-sm" dir="auto">
              {f.reason}
            </p>
          </blockquote>
        ))}
        {view.review &&
          view.review.report.combined.classification !== 'NO_ADDITIONAL_ISSUES' && (
            <p>
              {ui.combined}:{' '}
              <span dir="auto">{view.review.report.combined.reason}</span>
            </p>
          )}
      </Card>
      {error && <p role="alert">{ui.fail}</p>}
      {view.approval ? (
        <Card>
          <h3>{ui.approved}</h3>
          <p>{ui.production}</p>
          <time dateTime={view.approval.approvedAt}>
            {new Date(view.approval.approvedAt).toLocaleString(language)}
          </time>
        </Card>
      ) : (
        <Button
          variant="primary"
          className="mj-ws-script-approve"
          disabled={busy || !view.canApprove || readOnly}
          onClick={() => void approve()}
          aria-busy={busy}
        >
          {busy ? ui.approving : ui.approve}
        </Button>
      )}
    </section>
  );
}
